/**
 * Inline chat attachments — the allowlist, the size gate, and the two-step
 * upload. Ported from `bimgo-ai-web`'s `lib/apis`.
 *
 * **The upload is two hops, and the second one leaves our origins.** api-v2
 * mints a presigned pair (`POST /files/s3/generate-upload-url` →
 * `{ fileUrl, uploadUrl }`) and the bytes are then `PUT` **straight to S3**.
 * `/ask` only ever carries the resulting `fileUrl`.
 *
 * That second hop is a **CSP dependency this app has not settled yet**: the
 * planned policy (design §CSP) lists `connect-src 'self' <supabase> <api-v2>
 * <loopback>` and **no S3 origin**, so the PUT would be blocked the moment the
 * policy ships. Recorded against item AE6b — the transport is written here and
 * tested with an injected `fetch`, but it cannot be called from a CSP-enforcing
 * build until the origin is added or the upload is proxied through api-v2.
 *
 * Two rules kept from the reference because they are not cosmetic:
 *
 *   - **Validate every file before uploading any of them.** One unsupported
 *     file rejects the batch before a single byte moves, so a user does not pay
 *     for three uploads and then get told the fourth was never allowed.
 *   - **An aborted upload is not a failed one.** Removing a chip mid-upload
 *     aborts the PUT so no orphaned S3 object is left behind, and that abort
 *     must stay distinguishable from a real failure.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";

export type AttachmentKind = "image" | "pdf" | "excel" | "dwg";

export interface Attachment {
  url: string;
  kind: AttachmentKind;
  filename: string;
  size: number;
  /** Set for a DWG picked on this computer. It is not uploaded. */
  localPath?: string;
}

export type AttachmentErrorCode = "unsupported_type" | "exceeds_size";

export class AttachmentValidationError extends Error {
  readonly code: AttachmentErrorCode;
  readonly filename: string;
  readonly limitMB: number | null;
  constructor(code: AttachmentErrorCode, filename: string, message: string, limitMB?: number) {
    super(message);
    this.name = "AttachmentValidationError";
    this.code = code;
    this.filename = filename;
    this.limitMB = limitMB ?? null;
  }
}

/**
 * Per-kind allowlist. **Index 0 of `mimeTypes` is a fallback Content-Type**:
 * some browsers leave `file.type` empty for `.xlsx`, and the presign endpoint
 * needs a type it recognises.
 */
export const KIND_CONFIG: Record<AttachmentKind, { mimeTypes: string[]; extensions: string[] }> = {
  image: {
    mimeTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
  },
  pdf: { mimeTypes: ["application/pdf"], extensions: [".pdf"] },
  excel: {
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ],
    extensions: [".xlsx", ".xls"],
  },
  dwg: { mimeTypes: ["application/acad", "application/x-dwg", "image/vnd.dwg"], extensions: [".dwg"] },
};

/** The `accept` attribute for the composer's file input. */
export const ACCEPT_ATTR = Object.values(KIND_CONFIG)
  .flatMap((c) => [...c.mimeTypes, ...c.extensions])
  .join(",");

/** The web client's default; overridable there by env, fixed here for now. */
export const MAX_ATTACHMENT_MB = 10;

/** MIME first, extension as the fallback. Null means "not on the allowlist". */
export function detectFileKind(file: { name: string; type: string }): AttachmentKind | null {
  const lower = file.name.toLowerCase();
  const ext = `.${lower.split(".").pop() ?? ""}`;
  for (const [kind, cfg] of Object.entries(KIND_CONFIG)) {
    if (cfg.mimeTypes.includes(file.type) || cfg.extensions.includes(ext)) {
      return kind as AttachmentKind;
    }
  }
  return null;
}

/**
 * The kind of an attachment already on a message.
 *
 * History replay has only the URL — the backend persists it in the legacy
 * image-only column — so this falls back to `image`, and it strips the query
 * string first because a presigned GET's signature params would otherwise hide
 * the extension.
 */
export function detectKindFromUrl(url: string): AttachmentKind {
  const path = (url.toLowerCase().split("?")[0] ?? "");
  if (KIND_CONFIG.pdf.extensions.some((e) => path.endsWith(e))) return "pdf";
  if (KIND_CONFIG.excel.extensions.some((e) => path.endsWith(e))) return "excel";
  if (KIND_CONFIG.dwg.extensions.some((e) => path.endsWith(e))) return "dwg";
  return "image";
}

/**
 * A DWG the user picked, as a local path AutoCAD can open.
 *
 * The page's file input reports `C:\\fakepath\\...`, which is not a file.
 * Anything that is not an absolute `.dwg` path is refused rather than uploaded.
 */
export function localDrawingFromPath(
  file: { name: string; size: number },
  path: string,
): Attachment | null {
  const trimmed = path.trim();
  if (!trimmed || trimmed.toLowerCase().includes("fakepath")) return null;
  if (!trimmed.toLowerCase().endsWith(".dwg")) return null;
  return { url: "", kind: "dwg", filename: file.name, size: file.size, localPath: trimmed };
}

/**
 * The name to show for an attachment the transcript only has a URL for.
 *
 * The query string goes first for the same reason as in `detectKindFromUrl` — a
 * presigned GET carries its signature there — and the last path segment is
 * decoded, because the uploader percent-encodes spaces and CJK filenames and a
 * chip reading `%E5%9C%96.png` names nothing.
 *
 * A URL that ends in a slash, or is nothing but an origin, has no segment to
 * take; those answer `""` and the caller shows the kind alone rather than an
 * empty chip.
 */
export function filenameFromUrl(url: string): string {
  const path = url.split("?")[0] ?? "";
  const segment = path.split("/").pop() ?? "";
  try {
    return decodeURIComponent(segment);
  } catch {
    // A stray `%` that is not a valid escape throws rather than decoding. The
    // raw segment is still a better name than nothing.
    return segment;
  }
}

/**
 * One attachment as the transcript shows it — what survives a reload.
 *
 * Deliberately *not* `Attachment`: that type carries `size` and an upload
 * `status`, neither of which exists once the message is history. Modelling the
 * sent form separately is what stops a chip in the transcript pretending it can
 * still be removed or retried.
 */
export interface MessageAttachment {
  url: string;
  kind: AttachmentKind;
  filename: string;
}

/** A staged upload, as it will be shown once the message is sent. */
export function sentAttachment(attachment: Attachment): MessageAttachment {
  return { url: attachment.url, kind: attachment.kind, filename: attachment.filename };
}

/**
 * A stored message's text as the transcript shows it. The agent files an
 * attachments-only message as `[Attachments]` (api-v2 writes no user row for
 * empty text); that is history metadata, so beside its chips it reads as nothing.
 */
export function storedMessageText(content: string | null | undefined, attachments: readonly MessageAttachment[]): string {
  return attachments.length > 0 && content === "[Attachments]" ? "" : (content ?? "");
}

/**
 * History's `images` column — a bare URL list — as chips.
 *
 * The backend persists attachments as URLs only (`ThreadMessage.images`), so
 * everything else is inferred here. Blank entries are dropped rather than
 * rendered as an empty chip.
 */
export function sentAttachmentsFromUrls(urls: readonly string[] | null | undefined): MessageAttachment[] {
  if (!urls) return [];
  return urls
    .filter((url) => typeof url === "string" && url.trim() !== "")
    .map((url) => ({ url, kind: detectKindFromUrl(url), filename: filenameFromUrl(url) }));
}

/** Throws `AttachmentValidationError`; returns the kind and the content type. */
export function validateAttachment(
  file: { name: string; type: string; size: number },
  maxSizeMB = MAX_ATTACHMENT_MB,
): { kind: AttachmentKind; contentType: string } {
  const kind = detectFileKind(file);
  if (!kind) {
    throw new AttachmentValidationError(
      "unsupported_type",
      file.name,
      `Unsupported file type: ${file.name}`,
    );
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new AttachmentValidationError(
      "exceeds_size",
      file.name,
      `File "${file.name}" exceeds the ${maxSizeMB} MB limit for chat attachments.`,
      maxSizeMB,
    );
  }
  return { kind, contentType: file.type || (KIND_CONFIG[kind].mimeTypes[0] as string) };
}

export interface UploadDeps {
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

/** One file: presign against api-v2, then PUT the bytes to S3. */
export async function uploadAttachment(
  file: File,
  token: string | null,
  deps: UploadDeps,
  maxSizeMB = MAX_ATTACHMENT_MB,
): Promise<Attachment> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");
  const { kind, contentType } = validateAttachment(file, maxSizeMB);
  const fetchImpl = deps.fetchImpl ?? fetch;

  const presignUrl = `${deps.apiBaseUrl.replace(/\/+$/, "")}/files/s3/generate-upload-url`;
  const presignResponse = await fetchImpl(presignUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: bearer(token) },
    body: JSON.stringify({ contentType }),
  });
  const presignBody: unknown = await presignResponse.json().catch(() => null);
  if (!presignResponse.ok) {
    const detail = readDetail(presignBody);
    throw new OrganizationsError(
      { kind: "http", status: presignResponse.status, detail },
      detail ?? `could not prepare the upload (HTTP ${presignResponse.status})`,
    );
  }
  const { fileUrl, uploadUrl } = (presignBody ?? {}) as {
    fileUrl?: string;
    uploadUrl?: string;
  };
  if (!fileUrl || !uploadUrl) {
    throw new OrganizationsError(
      { kind: "http", status: presignResponse.status, detail: null },
      "the upload URL came back incomplete",
    );
  }

  // The presign call above cannot be aborted, so a remove during it would
  // otherwise still push the file to S3 and orphan the object.
  if (deps.signal?.aborted) throw new DOMException("Upload aborted", "AbortError");

  const put = await fetchImpl(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
    ...(deps.signal ? { signal: deps.signal } : {}),
  });
  if (!put.ok) {
    throw new OrganizationsError(
      { kind: "http", status: put.status, detail: null },
      `the upload was refused (HTTP ${put.status})`,
    );
  }

  return { url: fileUrl, kind, filename: file.name, size: file.size };
}

/**
 * A whole batch. **Every file is validated before any upload starts** — see the
 * header — and the uploads then run in parallel.
 */
export async function uploadAttachments(
  files: readonly File[],
  token: string | null,
  deps: UploadDeps,
  maxSizeMB = MAX_ATTACHMENT_MB,
): Promise<Attachment[]> {
  if (files.length === 0) return [];
  for (const file of files) validateAttachment(file, maxSizeMB);
  return Promise.all(files.map((file) => uploadAttachment(file, token, deps, maxSizeMB)));
}
